/**
 * epgCardContainer.tsx
 */
import Screen from '@shared/utils/screen';
import styled from 'styled-components';

const BASE_COLOR = '#545454';
const CATCHUP_COLOR = '#373737';
const FILL_COLOR = '#16AB8A';
const BORDER_WIDTH = 4 * Screen.widthRatio;
const PADDING_WIDTH = 24 * Screen.widthRatio;
const MARGIN_WIDTH = 2 * Screen.widthRatio;

type EpgCardProps = {
  displayContent?: boolean;
  hasFocus?: boolean;
  isPast?: boolean;
  progress?: number;
  width?: number; // % of width to fill
};

// `width` and `progress` are high-cardinality values (unique per EPG card) that would generate
// hundreds of CSS classes if interpolated in the template literal. They are moved to inline
// `style` via `.attrs()` so styled-components generates a single class for this component.
const EpgCardContainer = styled.div.attrs<EpgCardProps>((props) => {
  return {
    style: {
      // Progress gradient splits the card visually between elapsed (FILL_COLOR) and remaining (BASE_COLOR)
      background: props.isPast
        ? CATCHUP_COLOR
        : [
            `linear-gradient(to right, ${
              props.progress
                ? `${FILL_COLOR} 0%, ${FILL_COLOR} ${props.progress}px,`
                : ''
            } ${BASE_COLOR} ${
              props.progress
            }px, ${BASE_COLOR} 100%) padding-box`,
            `linear-gradient(to right, ${FILL_COLOR} 0%, ${FILL_COLOR} ${
              props.progress ? props.progress + BORDER_WIDTH : 0
            }px, ${BASE_COLOR} ${
              props.progress ? props.progress + BORDER_WIDTH : 0
            }px, ${BASE_COLOR} 100%) border-box`,
          ].join(', '),
      padding: `0px ${props.displayContent ? PADDING_WIDTH : 0}px`,
      // Dynamic width derived from program duration as a percentage of the timeline
      width: `calc(${Math.min(props.width ?? 0, 100)}% - ${
        BORDER_WIDTH * 2
      }px - ${
        props.displayContent ? PADDING_WIDTH * 2 : 0
      }px - ${MARGIN_WIDTH}px)`,
    },
  };
})<EpgCardProps>`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  float: left;
  justify-content: left;
  overflow: hidden;
  border: ${BORDER_WIDTH}px solid
    ${(props) => (props.hasFocus ? 'white' : 'transparent')};
  border-radius: ${20 * Screen.widthRatio}px;
  height: ${72 * Screen.widthRatio}px;
  margin-right: ${MARGIN_WIDTH * Screen.widthRatio}px;
  &:hover {
    border: ${BORDER_WIDTH}px solid white;
  }
`;

export default EpgCardContainer;
